import {
  useInvoiceRenderContext,
  type InvoiceRenderContext,
} from '../api/use-invoice-render-context';

// The invoice-bound figures shown wherever an operator reviews or edits a
// document: counterparty, weight, price, amount, and currency, resolved through
// the same assembly the PDF binds — never the row's stored figures, which can
// lag the document (a price fixed after the draft was created, a payable
// factor applied at render time).

// Always-shown rows, in document order.
const FIGURE_ROWS: readonly { readonly key: string; readonly label: string }[] = [
  { key: 'counterparty_name', label: 'Counterparty' },
  { key: 'invoice_number_display', label: 'Invoice number' },
  { key: 'currency', label: 'Currency' },
  { key: 'total_net_weight_formatted', label: 'Weight' },
  { key: 'unit_price_formatted', label: 'Unit price' },
  { key: 'total_amount_formatted', label: 'Amount' },
];

// Pricing-derivation rows that only some kinds carry (the BL body renders them
// for provisional/final pricing) — omitted entirely when the document has none
// rather than showing an em-dash pair.
const DERIVATION_ROWS: readonly { readonly key: string; readonly label: string }[] = [
  { key: 'base_price_formatted', label: 'Base price' },
  { key: 'payable_factor', label: 'Payable factor' },
];

// The value the document renders for a path: the operator override where one
// exists, else the system resolution. The assembler's own em-dash placeholder
// counts as absent so optional rows can be dropped.
function documentValue(context: InvoiceRenderContext | undefined, key: string): string | null {
  const raw = context?.overrides[key] ?? context?.values[key];
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  return text === '' || text === '—' ? null : text;
}

function FigureRow({ label, value }: Readonly<{ label: string; value: string | null }>) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value ?? '—'}</span>
    </div>
  );
}

// The read-only figure rows for one invoice, self-fetching so every surface
// (review dialog, edit dialogs) shows the same resolved values from the same
// cached read.
export function DocumentFigureRows({ invoiceId }: Readonly<{ invoiceId: string }>) {
  const query = useInvoiceRenderContext(invoiceId);
  if (query.isError) {
    return (
      <p className="text-muted-foreground py-1 text-xs" data-testid="document-figures-error">
        The resolved document values are unavailable right now.
      </p>
    );
  }
  const pending = query.data === undefined;
  const calcLine = documentValue(query.data, 'worked_calc_line');
  return (
    <div data-testid="document-figures">
      {FIGURE_ROWS.map(({ key, label }) => (
        <FigureRow key={key} label={label} value={pending ? '…' : documentValue(query.data, key)} />
      ))}
      {DERIVATION_ROWS.map(({ key, label }) => {
        const value = documentValue(query.data, key);
        return value === null ? null : <FigureRow key={key} label={label} value={value} />;
      })}
      {calcLine !== null ? (
        <p
          className="text-muted-foreground pt-1 text-right text-xs"
          data-testid="document-figures-calc"
        >
          {calcLine}
        </p>
      ) : null}
    </div>
  );
}

// The "what the document shows" card for the edit dialogs' right pane: weight,
// price, and counterparty change upstream and the document is regenerated —
// these figures confirm what currently stands, they are never typed over.
export function DocumentFiguresCard({ invoiceId }: Readonly<{ invoiceId: string }>) {
  return (
    <section aria-labelledby="document-figures-heading" className="mb-4 flex flex-col gap-2">
      <h3 id="document-figures-heading" className="text-sm font-semibold">
        On the document
      </h3>
      <div className="bg-muted/40 rounded-md border p-3">
        <DocumentFigureRows invoiceId={invoiceId} />
      </div>
      <p className="text-muted-foreground text-xs">
        Resolved from the trade data — saving your changes regenerates these values.
      </p>
    </section>
  );
}
